/**
 * Visit header Billing chip (PRD.163 Section 6).
 * Compact status + amount summary that opens the VisitBillingPanel on click.
 */

import { useMemo } from 'react';
import { NavIcon } from '../../../shell/shared';
import { usePowerSyncQuery } from '../../../adapters/powersync';
import { qInvoicesByEncounter, type InvoiceRow } from '@helios/data-access';
import { isPositive, phpFromMajor, sumMoney, type Money } from '@helios/domain';
import { useFormatMoney } from '../../../hooks/useFormatMoney';

// ─── Helpers ───
//
// PRD.218 — invoices.grand_total is stored as decimal-major (₱100 → 100),
// not minor units, so we convert via phpFromMajor at the boundary and
// let Dinero handle the arithmetic. The "paid so far" total comes from
// invoice.status only in this chip (no payment-row join); we'll switch
// to summing invoice_payments per-invoice in a follow-up if the chip
// needs partial-payment fidelity, but the queue/detail surfaces already
// use the real payments table.

interface Summary {
  label: string;
  tone: 'neutral' | 'draft' | 'balance' | 'paid';
}

function summarize(invoices: InvoiceRow[], fmtCompact: (m: Money) => string): Summary {
  if (invoices.length === 0) return { label: 'New', tone: 'neutral' };

  const chargedList: Money[] = [];
  const paidList: Money[] = [];
  let hasDraft = false;
  for (const inv of invoices) {
    const total = phpFromMajor(inv.grand_total ?? 0);
    chargedList.push(total);
    if (inv.status === 'paid') paidList.push(total);
    if (inv.status === 'draft') hasDraft = true;
  }
  const totalCharged = sumMoney(chargedList);
  const totalPaid = sumMoney(paidList);
  const balance = totalCharged.subtract(totalPaid);

  if (!isPositive(balance) && isPositive(totalCharged)) return { label: 'Paid', tone: 'paid' };
  if (hasDraft && !isPositive(totalCharged)) return { label: 'Draft', tone: 'draft' };
  return { label: `Balance ${fmtCompact(balance)}`, tone: 'balance' };
}

const TONE_STYLES: Record<Summary['tone'], { bg: string; color: string; border: string }> = {
  neutral: {
    bg: 'var(--bg-surface)',
    color: 'var(--text-muted)',
    border: 'var(--border)',
  },
  draft: {
    bg: 'var(--color-warning-bg)',
    color: 'var(--color-warning-text)',
    border: 'var(--color-warning-border)',
  },
  balance: {
    bg: 'var(--color-warning-bg)',
    color: 'var(--color-warning-text)',
    border: 'var(--color-warning-border)',
  },
  paid: {
    bg: 'var(--color-success-bg)',
    color: 'var(--color-success-text)',
    border: 'var(--color-success-border, var(--border))',
  },
};

// ─── Props ───

interface Props {
  patientId: string;
  encounterId: string;
  onClick: () => void;
}

// ─── Component ───

export function VisitBillingChip({ patientId, encounterId, onClick }: Props) {
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(
    qInvoicesByEncounter(patientId, encounterId),
  );
  const fmtCompact = useFormatMoney({ compact: true });

  const summary = useMemo(() => summarize(invoiceRows, fmtCompact), [invoiceRows, fmtCompact]);
  const tone = TONE_STYLES[summary.tone];

  return (
    <button
      onClick={onClick}
      title="Open Billing"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        height: 32,
        padding: '0 10px',
        borderRadius: 'var(--radius-full)',
        border: `1px solid ${tone.border}`,
        background: tone.bg,
        color: tone.color,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 500,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
    >
      <NavIcon name="credit-card" />
      <span style={{ color: 'var(--text)', fontWeight: 600 }}>Billing</span>
      <span style={{ opacity: 0.55 }}>·</span>
      <span>{summary.label}</span>
    </button>
  );
}
